import { Editor, NodePos } from '@tiptap/vue-2';

/**
 * Takes a style string and converts it into an object mapping by
 * separating key value pairs by semicolon.
 * @param style
 * @returns
 */
export const convertStyleStringToObject = (style?: string) => {
  if (!style) {
    return {};
  }

  return style.split(';').reduce<Record<string, string>>((acc, cur) => {
    const [key, value] = cur.split(':');
    return {
      ...acc,
      [key]: value,
    };
  }, {});
};

/**
 * Takes a standard styles `Record<string, string>` and turns it into
 * a style string that would be found in the style HTML Attribute
 * @param styles
 * @returns
 */
export const convertStyleObjectToString = (styles?: Record<string, string | null>) => {
  if (!styles) {
    return '';
  }

  return Object
    .entries(styles)
    .map<string>(([key, val]) => (val ? `${key}:${val.trim()}` : ''))
    .filter((val) => val.length > 0)
    .join(';');
};

/**
 * Find nearest NodePos and give its element a type
 * NOTE: Only works for `Node`, not `Marks`
 */
export const findNodePosByElement = <T extends HTMLElement>(
  editor: Editor,
  position: number,
  name: string,
): { nodePos?: NodePos | null, element?: T } => {
  const nodePos = editor.$pos(position);

  if (nodePos?.element?.tagName?.toUpperCase() === name.toUpperCase()) {
    return {
      nodePos,
      element: nodePos.element as T,
    };
  }

  const closestNode = nodePos.closest(name);

  return {
    nodePos: closestNode ?? undefined,
    element: closestNode?.element as T,
  };
};

/**
 * Updates attributes of given `NodePos`, which is essentially the element found at the selection's position.
 * I've found this to be more reliable than the editor's `updateAttributes` chain command for tables.
 * Also calls `setAttribute` after setting attribute keys directly.
 * Setting attributes directly forces styling to change immediately.
 * Calling `setAttribute` forces TipTap's `update` event to run to reflect the changes in v-model.
 * @param nodePos
 * @param newValue
 */
export const setNodePosAttributes = (
  nodePos: NodePos,
  newValue: Record<string, string | null>,
) => {
  Object.entries(newValue).forEach(([ key, value ]) => {
    nodePos.attributes[key] = value;
  });
};
